import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '../../shared/components/Skeleton';
import { downloadCsv } from '../../shared/lib/csv';
import { formatBRL, formatDateTime } from '../../shared/lib/format';
import {
  relatoriosApi,
  type CategoriaVendaRow,
  type OperadorPerformanceRow,
  type PagamentoPorFormaRow,
  type ProdutoTopRow,
  type VendaDetalhadaRow,
  type VendasPorDiaRow,
} from './api';

const FORMA_LABEL: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Cartao Debito',
  cartao_credito: 'Cartao Credito',
  vale_refeicao: 'Vale Refeicao',
  outro: 'Outro',
};

type TabId =
  | 'vendas_dia'
  | 'top_produtos'
  | 'pagamentos'
  | 'categorias'
  | 'operadores'
  | 'vendas_detalhadas';

const TABS: { id: TabId; label: string }[] = [
  { id: 'vendas_dia', label: 'Vendas por dia' },
  { id: 'top_produtos', label: 'Top produtos' },
  { id: 'pagamentos', label: 'Formas de pagamento' },
  { id: 'categorias', label: 'Categorias' },
  { id: 'operadores', label: 'Operadores' },
  { id: 'vendas_detalhadas', label: 'Vendas (detalhado)' },
];

function toLocalInputDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function RelatoriosPage() {
  const today = useMemo(() => new Date(), []);
  const last30 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d;
  }, []);

  const [inicio, setInicio] = useState(toLocalInputDate(last30));
  const [fim, setFim] = useState(toLocalInputDate(today));
  const [tab, setTab] = useState<TabId>('vendas_dia');

  const periodo = useMemo(() => {
    const i = new Date(`${inicio}T00:00:00`);
    const f = new Date(`${fim}T23:59:59`);
    return { inicio: i.toISOString(), fim: f.toISOString() };
  }, [inicio, fim]);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Relatorios</h1>
        <p className="mt-1 text-sm text-slate-500">
          Analise o desempenho da loja. Filtre por periodo e exporte para CSV.
        </p>
      </header>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Inicio
          </label>
          <input
            type="date"
            className="input"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
            Fim
          </label>
          <input
            type="date"
            className="input"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
          />
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => {
              const d = new Date();
              setInicio(toLocalInputDate(d));
              setFim(toLocalInputDate(d));
            }}
          >
            Hoje
          </button>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => {
              const d = new Date();
              d.setDate(d.getDate() - 6);
              setInicio(toLocalInputDate(d));
              setFim(toLocalInputDate(new Date()));
            }}
          >
            7 dias
          </button>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => {
              const d = new Date();
              d.setDate(d.getDate() - 29);
              setInicio(toLocalInputDate(d));
              setFim(toLocalInputDate(new Date()));
            }}
          >
            30 dias
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              tab === t.id
                ? 'rounded-t-md border-b-2 border-brand-600 px-3 py-2 text-sm font-medium text-brand-700'
                : 'rounded-t-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'vendas_dia' && <VendasDiaTab periodo={periodo} />}
      {tab === 'top_produtos' && <TopProdutosTab periodo={periodo} />}
      {tab === 'pagamentos' && <PagamentosTab periodo={periodo} />}
      {tab === 'categorias' && <CategoriasTab periodo={periodo} />}
      {tab === 'operadores' && <OperadoresTab periodo={periodo} />}
      {tab === 'vendas_detalhadas' && <VendasDetalhadasTab periodo={periodo} />}
    </div>
  );
}

interface TabProps {
  periodo: { inicio: string; fim: string };
}

function VendasDiaTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'vendas-dia', periodo],
    queryFn: () => relatoriosApi.vendasPorDia(periodo),
  });
  const rows = data?.rows ?? [];
  const total = rows.reduce((a, r) => a + Number(r.total), 0);

  return (
    <Card
      title="Vendas por dia"
      onExport={() =>
        downloadCsv<VendasPorDiaRow>(
          'vendas-por-dia',
          rows,
          [
            { key: 'dia', label: 'Dia', format: (r) => new Date(r.dia).toLocaleDateString('pt-BR') },
            { key: 'qtd_vendas', label: 'Vendas' },
            { key: 'total', label: 'Receita (R$)' },
            { key: 'desconto', label: 'Desconto (R$)' },
            { key: 'ticket_medio', label: 'Ticket medio (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <div className="mb-3 text-sm text-slate-600">
        Total no periodo: <strong className="text-brand-700">{formatBRL(total)}</strong>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows.map((r) => ({
              dia: new Date(r.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
              total: Number(r.total),
              vendas: Number(r.qtd_vendas),
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="dia" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `R$${v.toFixed(0)}`} />
            <Tooltip formatter={(v: unknown) => formatBRL(Number(v))} />
            <Bar dataKey="total" fill="#6d28d9" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <DataTable
        rows={rows}
        columns={[
          { label: 'Dia', get: (r) => new Date(r.dia).toLocaleDateString('pt-BR') },
          { label: 'Vendas', get: (r) => String(r.qtd_vendas) },
          { label: 'Ticket medio', get: (r) => formatBRL(Number(r.ticket_medio)) },
          { label: 'Total', get: (r) => formatBRL(Number(r.total)), align: 'right' },
        ]}
      />
    </Card>
  );
}

function TopProdutosTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'top-produtos', periodo],
    queryFn: () => relatoriosApi.topProdutos({ ...periodo, limit: 50 }),
  });
  const rows = data?.rows ?? [];

  return (
    <Card
      title="Produtos mais vendidos"
      onExport={() =>
        downloadCsv<ProdutoTopRow>(
          'top-produtos',
          rows,
          [
            { key: 'produto_nome', label: 'Produto' },
            { key: 'quantidade_total', label: 'Qtd' },
            { key: 'vendas', label: 'Num. vendas' },
            { key: 'receita_total', label: 'Receita (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <DataTable
        rows={rows}
        columns={[
          { label: '#', get: (_r, i) => String(i + 1) },
          { label: 'Produto', get: (r) => r.produto_nome },
          { label: 'Qtd', get: (r) => Number(r.quantidade_total).toLocaleString('pt-BR') },
          { label: 'Vendas', get: (r) => String(r.vendas) },
          { label: 'Receita', get: (r) => formatBRL(Number(r.receita_total)), align: 'right' },
        ]}
      />
    </Card>
  );
}

function PagamentosTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'pagamentos', periodo],
    queryFn: () => relatoriosApi.pagamentosPorForma(periodo),
  });
  const rows = data?.rows ?? [];
  const COLORS = ['#6d28d9', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#64748b'];

  return (
    <Card
      title="Formas de pagamento"
      onExport={() =>
        downloadCsv<PagamentoPorFormaRow>(
          'pagamentos-por-forma',
          rows,
          [
            { key: 'forma', label: 'Forma', format: (r) => FORMA_LABEL[r.forma] ?? r.forma },
            { key: 'qtd', label: 'Pagamentos' },
            { key: 'total', label: 'Total (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={rows.map((r) => ({ name: FORMA_LABEL[r.forma] ?? r.forma, value: Number(r.total) }))}
                dataKey="value"
                nameKey="name"
                outerRadius={90}
                label={(entry: { name?: string; percent?: number }) =>
                  `${entry.name ?? ''} ${Math.round((entry.percent ?? 0) * 100)}%`
                }
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: unknown) => formatBRL(Number(v))} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <DataTable
          rows={rows}
          columns={[
            { label: 'Forma', get: (r) => FORMA_LABEL[r.forma] ?? r.forma },
            { label: 'Pagamentos', get: (r) => String(r.qtd) },
            { label: 'Total', get: (r) => formatBRL(Number(r.total)), align: 'right' },
          ]}
        />
      </div>
    </Card>
  );
}

function CategoriasTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'categorias', periodo],
    queryFn: () => relatoriosApi.vendasPorCategoria(periodo),
  });
  const rows = data?.rows ?? [];

  return (
    <Card
      title="Vendas por categoria"
      onExport={() =>
        downloadCsv<CategoriaVendaRow>(
          'vendas-por-categoria',
          rows,
          [
            { key: 'categoria_nome', label: 'Categoria' },
            { key: 'quantidade', label: 'Qtd' },
            { key: 'receita', label: 'Receita (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="categoria_nome" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `R$${v.toFixed(0)}`} />
            <Tooltip formatter={(v: unknown) => formatBRL(Number(v))} />
            <Bar dataKey="receita" radius={[6, 6, 0, 0]}>
              {rows.map((c) => (
                <Cell key={c.categoria_id} fill={c.cor || '#6d28d9'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <DataTable
        rows={rows}
        columns={[
          { label: 'Categoria', get: (r) => r.categoria_nome },
          { label: 'Qtd', get: (r) => Number(r.quantidade).toLocaleString('pt-BR') },
          { label: 'Receita', get: (r) => formatBRL(Number(r.receita)), align: 'right' },
        ]}
      />
    </Card>
  );
}

function OperadoresTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'operadores', periodo],
    queryFn: () => relatoriosApi.operadorPerformance(periodo),
  });
  const rows = data?.rows ?? [];

  return (
    <Card
      title="Performance por operador"
      onExport={() =>
        downloadCsv<OperadorPerformanceRow>(
          'operadores',
          rows,
          [
            { key: 'nome', label: 'Operador' },
            { key: 'email', label: 'Email' },
            { key: 'vendas', label: 'Vendas' },
            { key: 'ticket_medio', label: 'Ticket medio (R$)' },
            { key: 'receita', label: 'Receita (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <DataTable
        rows={rows}
        columns={[
          { label: 'Operador', get: (r) => r.nome },
          { label: 'Vendas', get: (r) => String(r.vendas) },
          { label: 'Ticket medio', get: (r) => formatBRL(Number(r.ticket_medio)) },
          { label: 'Receita', get: (r) => formatBRL(Number(r.receita)), align: 'right' },
        ]}
      />
    </Card>
  );
}

function VendasDetalhadasTab({ periodo }: TabProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rel', 'vendas-detalhadas', periodo],
    queryFn: () => relatoriosApi.vendasDetalhadas(periodo),
  });
  const rows = data?.rows ?? [];

  return (
    <Card
      title="Vendas detalhadas"
      onExport={() =>
        downloadCsv<VendaDetalhadaRow>(
          'vendas-detalhadas',
          rows,
          [
            { key: 'numero', label: 'Numero' },
            { key: 'criado_em', label: 'Data', format: (r) => formatDateTime(r.criado_em) },
            { key: 'status', label: 'Status' },
            { key: 'operador', label: 'Operador', format: (r) => r.operador?.nome ?? '' },
            {
              key: 'pagamentos',
              label: 'Pagamentos',
              format: (r) =>
                r.pagamentos
                  .map((p) => `${FORMA_LABEL[p.forma] ?? p.forma}: ${formatBRL(Number(p.valor))}`)
                  .join(' | '),
            },
            { key: 'subtotal', label: 'Subtotal (R$)' },
            { key: 'desconto', label: 'Desconto (R$)' },
            { key: 'total', label: 'Total (R$)' },
          ],
        )
      }
      isLoading={isLoading}
      empty={rows.length === 0}
    >
      <DataTable
        rows={rows}
        columns={[
          { label: '#', get: (r) => `#${r.numero}` },
          { label: 'Data', get: (r) => formatDateTime(r.criado_em) },
          {
            label: 'Status',
            get: (r) => (
              <span
                className={
                  r.status === 'paga'
                    ? 'rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700'
                    : 'rounded-full bg-red-50 px-2 py-0.5 text-[11px] text-red-700'
                }
              >
                {r.status}
              </span>
            ),
          },
          { label: 'Operador', get: (r) => r.operador?.nome ?? '-' },
          { label: 'Total', get: (r) => formatBRL(Number(r.total)), align: 'right' },
        ]}
      />
    </Card>
  );
}

function Card(props: {
  title: string;
  onExport: () => void;
  isLoading: boolean;
  empty: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">{props.title}</h2>
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={props.onExport}
          disabled={props.isLoading || props.empty}
        >
          <Download className="h-4 w-4" />
          Exportar CSV
        </button>
      </div>
      {props.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : props.empty ? (
        <p className="py-10 text-center text-sm text-slate-400">
          Sem dados para o periodo selecionado.
        </p>
      ) : (
        props.children
      )}
    </div>
  );
}

interface Column<T> {
  label: string;
  get: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'right';
}

function DataTable<T>({ rows, columns }: { rows: T[]; columns: Column<T>[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <div className="mt-4 hidden overflow-x-auto rounded-lg border border-slate-200 md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.label}
                  className={`px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td
                    key={c.label}
                    className={`whitespace-nowrap px-3 py-2 text-slate-700 ${c.align === 'right' ? 'text-right font-medium' : ''}`}
                  >
                    {c.get(r, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="mt-3 grid gap-2 md:hidden">
        {rows.map((r, i) => (
          <li key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
            {columns.map((c) => (
              <div key={c.label} className="flex justify-between gap-2 py-0.5">
                <span className="text-xs text-slate-500">{c.label}</span>
                <span className="text-right font-medium text-slate-800">{c.get(r, i)}</span>
              </div>
            ))}
          </li>
        ))}
      </ul>
    </>
  );
}

